import api, { crmApiUrl } from "../../../crm/lib/api.js";
import { saveBlob } from "../../../lib/download.js";

/**
 * A prospect's attachments — crm-api's `LeadAttachmentsController` (spec
 * §4.4), which is `CustomerAttachmentsController` mounted under `leads`.
 *
 * A PORTAL FILE, not a ported one, because xop-edge-staff has no counterpart:
 * lead attachments exist because a RESELLER needed somewhere to keep its own
 * documents about a lead (spec §1). It goes through the ported axios instance
 * (`crm/lib/api.ts`) rather than the portal's `fetch` wrapper for two
 * reasons: the upload is `multipart/form-data`, where the browser must set
 * the boundary itself, and the download is a blob. Both are things that
 * client already does for the customer twin.
 *
 * The METHOD SURFACE is `customerAttachmentsApi`'s, key for key, because the
 * panel below takes either one through its `api` prop. Keep them in step: a
 * method added to one and not the other fails only at runtime, in the tree
 * that is not typechecked.
 */

export interface AttachmentRecord {
  id: string;
  name: string;
  original_filename: string;
  content_type: string;
  file_size: number;
  created_at: string;
  uploaded_by: { id: string; name: string };
  /**
   * Whether THIS reseller may destroy this row — crm-api's own answer, not a
   * guess made here. It refuses a delete of a blob the reseller did not
   * upload (spec §4.4), so a row uploaded by XOP staff comes back `false`
   * even for someone holding the write key.
   */
  deletable?: boolean;
  uploaded_by_email?: string;
}

export interface AttachmentsListResponse {
  success: boolean;
  attachments: AttachmentRecord[];
  pagination?: { current_page: number; per_page: number; total_count: number };
  error?: string;
}

export interface AttachmentUploadResponse {
  success: boolean;
  attachment: AttachmentRecord;
  message?: string;
  error?: string;
}

/** The shape `<FileManagerPanel api={…}>` accepts — satisfied by both
 * `customerAttachmentsApi` and `leadAttachmentsApi`. */
export interface AttachmentsApi {
  getAttachments(
    ownerId: string,
    params?: { page?: number; per_page?: number; search?: string },
  ): Promise<AttachmentsListResponse>;
  uploadFile(ownerId: string, file: File): Promise<AttachmentUploadResponse>;
  downloadFile(ownerId: string, attachmentId: string, filename?: string): Promise<void>;
  deleteFile(ownerId: string, attachmentId: string): Promise<{ success: boolean; error?: string }>;
  getFileContent(ownerId: string, attachmentId: string): Promise<{ data: string }>;
  /**
   * The absolute URL `CustomerAttachmentViewer`'s blob-fetching branches
   * (image/video/audio, and PDF/Excel/Word in `loadDocumentContent`) hit
   * with a raw `fetch` — those need a URL string, not an axios call, because
   * the response is read as a blob/arrayBuffer rather than JSON. Scoped so
   * the viewer never has to guess a route from the customer/lead split
   * itself. Goes through `crmApiUrl` (the BFF's `/api/crm/v1` passthrough)
   * so it lands on `/api/crm/v1/<owner>/:id/attachments/:aid/view`, not a
   * doubled `/api/v1` prefix.
   */
  viewUrl(ownerId: string, attachmentId: string): string;
  validateFile(file: File): { valid: boolean; error?: string };
  formatFileSize(bytes: number): string;
}

const MAX_BYTES = 50 * 1024 * 1024;

/** The same list `customer-attachments-api.ts` carries, restated rather than
 * imported: that file is a verbatim port and must stay diffable. */
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/svg+xml",
  "application/zip",
  "application/x-zip-compressed",
  "application/x-rar-compressed",
  "application/x-7z-compressed",
];

export const leadAttachmentsApi: AttachmentsApi = {
  async getAttachments(leadId, params) {
    const query = new URLSearchParams();
    if (params?.page) query.append("page", String(params.page));
    if (params?.per_page) query.append("per_page", String(params.per_page));
    if (params?.search) query.append("search", params.search);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    const response = await api.get(`/leads/${leadId}/attachments${suffix}`);
    return response.data as AttachmentsListResponse;
  },

  async uploadFile(leadId, file) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(`/leads/${leadId}/attachments/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data as AttachmentUploadResponse;
  },

  async downloadFile(leadId, attachmentId, filename) {
    const response = await api.get(`/leads/${leadId}/attachments/${attachmentId}/download`, {
      responseType: "blob",
    });
    await saveBlob(filename ?? `attachment-${attachmentId}`, new Blob([response.data as BlobPart]));
  },

  async deleteFile(leadId, attachmentId) {
    const response = await api.delete(`/leads/${leadId}/attachments/${attachmentId}`);
    return response.data as { success: boolean; error?: string };
  },

  async getFileContent(leadId, attachmentId) {
    const response = await api.get(`/leads/${leadId}/attachments/${attachmentId}/view`, {
      responseType: "text",
    });
    return { data: String(response.data) };
  },

  viewUrl(leadId, attachmentId) {
    return crmApiUrl(`/leads/${leadId}/attachments/${attachmentId}/view`);
  },

  validateFile(file) {
    if (file.size > MAX_BYTES) {
      return { valid: false, error: "File size too large. Maximum size is 50MB." };
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      return {
        valid: false,
        error:
          "Invalid file type. Only PDF, Office documents, images, text files and archives are allowed.",
      };
    }
    return { valid: true };
  },

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i] ?? "Bytes"}`;
  },
};
